import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { loadSnap } from "../lib/snap";
import { Check, Crown, Loader2, Zap } from "lucide-react";

const PRO = ["Cetak unlimited", "AI Auto-Parse (Gemini)", "Tanpa watermark", "Upload logo toko sendiri", "Bulk CSV/Excel hingga 100 baris", "Prioritas support"];

export function PricingSection({ open, onClose, proPrice = 29000, freeLimit = 10 }) {
  const { user, login, isPro, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const freeFeatures = [`Maks ${freeLimit} resi / hari`, "Parser cepat (regex)", "Semua ukuran kertas", "Ada watermark kecil", "CSV maks 5 baris"];

  const handleUpgrade = async () => {
    if (!user) { toast.info("Masuk dulu dengan Google untuk upgrade"); login(); return; }
    setLoading(true);
    try {
      await loadSnap();
      const { data } = await api.post("/payments/snap-token");
      // Close our Radix dialog first so its overlay/focus-trap doesn't block the Midtrans Snap popup
      onClose?.();
      await new Promise((r) => setTimeout(r, 300));
      window.snap.pay(data.token, {
        onSuccess: async () => {
          try {
            await api.get(`/payments/status/${data.order_id}`);
            await refresh();
            toast.success("Pembayaran berhasil! Akun Anda kini PRO 🎉");
            onClose?.();
          } catch { toast.info("Pembayaran diterima, status sedang diverifikasi."); }
        },
        onPending: () => toast.info("Pembayaran tertunda. Akun diupgrade setelah konfirmasi."),
        onError: () => toast.error("Pembayaran gagal. Coba lagi."),
        onClose: () => toast.message("Jendela pembayaran ditutup."),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memulai pembayaran");
    } finally {
      setLoading(false);
    }
  };

  const Plan = ({ title, price, features, pro, testid, children }) => (
    <div data-testid={testid}
      className={`rounded-xl border p-6 flex flex-col ${pro ? "border-orange-400 bg-orange-50/40 ring-1 ring-orange-200" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2">
        {pro && <Crown size={18} className="text-orange-500" />}
        <h3 className="font-extrabold text-lg text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
      </div>
      <div className="mt-2 mb-4">
        <span className="text-3xl font-extrabold text-slate-900">{price}</span>
        {pro && <span className="text-slate-500 text-sm">/bulan</span>}
      </div>
      <ul className="space-y-2 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <Check size={16} className={pro ? "text-orange-500 mt-0.5" : "text-sky-600 mt-0.5"} /> {f}
          </li>
        ))}
      </ul>
      {children}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="pricing-section-container" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Pilih Paket Anda
          </DialogTitle>
          <DialogDescription>Bandingkan paket Gratis dan Pro, lalu upgrade via Midtrans.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          <Plan title="Gratis" price="Rp0" features={freeFeatures} testid="plan-card-free">
            <Badge variant="outline" className="mt-4 justify-center py-1.5">Paket saat ini{!isPro ? "" : ""}</Badge>
          </Plan>
          <Plan title="Pro" price={`Rp${Number(proPrice).toLocaleString("id-ID")}`} features={PRO} pro testid="plan-card-pro">
            {isPro ? (
              <Button disabled className="mt-4 bg-orange-600 gap-2"><Crown size={16} /> Anda sudah Pro</Button>
            ) : (
              <Button data-testid="btn-subscribe-stripe-pro" onClick={handleUpgrade} disabled={loading}
                className="mt-4 bg-orange-600 hover:bg-orange-700 gap-2 font-semibold">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                {user ? "Upgrade Sekarang" : "Masuk & Upgrade"}
              </Button>
            )}
          </Plan>
        </div>
        <p className="text-xs text-center text-slate-400 mt-2">
          Pembayaran aman via Midtrans (QRIS, GoPay, Virtual Account, Kartu).
        </p>
      </DialogContent>
    </Dialog>
  );
}
